import React from 'react';
import { Link } from 'react-router-dom';
import {
  HiOutlinePhotograph,
  HiOutlineFilm,
  HiOutlineDocumentText,
  HiOutlineLightningBolt,
  HiOutlineCloudUpload,
  HiOutlineCode,
  HiOutlineShieldCheck,
  HiOutlineChartBar,
  HiOutlineArrowRight,
  HiOutlineCheck,
} from 'react-icons/hi';
import './Home.css';

const highlights = [
  { icon: <HiOutlinePhotograph />, title: 'Image Upscaling', desc: 'Upscale images 2x, 4x, or 8x with AI-powered detail enhancement and noise reduction.' },
  { icon: <HiOutlineFilm />, title: 'Video Enhancement', desc: 'Upscale videos to 4K/8K with frame interpolation, stabilization, and artifact removal.' },
  { icon: <HiOutlineDocumentText />, title: 'Document Cleanup', desc: 'Sharpen scans, remove noise, and prepare documents for OCR with one click.' },
  { icon: <HiOutlineLightningBolt />, title: 'Smart AI Modes', desc: 'Auto-detect content type and apply the perfect enhancement profile automatically.' },
  { icon: <HiOutlineCloudUpload />, title: 'Batch Processing', desc: 'Process 1000+ files at once with preset workflows and queue-based tracking.' },
  { icon: <HiOutlineCode />, title: 'API & SDKs', desc: 'Integrate AI enhancement into your apps with our REST API and Node.js / Python SDKs.' },
];

const stats = [
  { value: '8x', label: 'Max Upscale' },
  { value: '120fps', label: 'Frame Interpolation' },
  { value: '1000+', label: 'Batch Files' },
  { value: '<2s', label: 'Image Processing' },
];

const useCases = [
  { title: 'E-commerce', desc: 'Crisp product images with clean backgrounds that convert.', icon: '🛍️' },
  { title: 'Real Estate', desc: 'Enhance property photos and virtual tours automatically.', icon: '🏠' },
  { title: 'Content Creators', desc: 'Upscale and polish videos for YouTube, TikTok, and more.', icon: '🎬' },
  { title: 'Archivists', desc: 'Restore old photos and historical documents to clarity.', icon: '📜' },
  { title: 'Print & Publishing', desc: 'Upscale low-res images to print-ready quality.', icon: '🖨️' },
  { title: 'Healthcare', desc: 'Enhance medical scans and document imaging.', icon: '🏥' },
];

const plans = [
  {
    name: 'Free',
    price: '$0',
    period: 'forever',
    features: ['5 images/day', 'Up to 2x upscale', 'Basic noise reduction', 'JPG & PNG export', 'Community support'],
    cta: 'Start Free',
    highlighted: false,
  },
  {
    name: 'Pro',
    price: '$29',
    period: '/month',
    features: ['500 credits/month', 'Up to 8x upscale', 'Video processing', 'All AI modes', 'Priority support', 'API access (5k calls)'],
    cta: 'Go Pro',
    highlighted: true,
  },
  {
    name: 'Business',
    price: '$99',
    period: '/month',
    features: ['Unlimited credits', 'Team accounts (10 seats)', 'Batch processing', 'Full API access', 'Custom workflows', 'Dedicated support', 'SLA guarantee'],
    cta: 'Contact Sales',
    highlighted: false,
  },
];

export default function Home() {
  return (
    <div className="home">
      {/* Hero */}
      <section className="hero">
        <div className="hero__bg-orb hero__bg-orb--1" />
        <div className="hero__bg-orb hero__bg-orb--2" />
        <div className="container hero__content">
          <span className="hero__badge">Powered by Advanced AI</span>
          <h1 className="hero__title">
            Upscale &amp; Enhance<br />
            <span className="gradient-text">Everything with AI</span>
          </h1>
          <p className="hero__subtitle">
            Transform low-resolution images, videos, and documents into stunning high-quality outputs.
            Powered by state-of-the-art neural networks.
          </p>
          <div className="hero__actions">
            <Link to="/features" className="btn btn-primary">
              Explore Features <HiOutlineArrowRight />
            </Link>
            <Link to="/about" className="btn btn-secondary">
              Learn More
            </Link>
          </div>
          <div className="hero__stats">
            {stats.map(({ value, label }) => (
              <div key={label} className="hero__stat">
                <span className="hero__stat-value">{value}</span>
                <span className="hero__stat-label">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

    
      {/* Features Highlights */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Capabilities</span>
            <h2 className="section-title">Everything You Need to Enhance</h2>
            <p className="section-subtitle">
              A complete AI-powered toolkit for upscaling and enhancing any media type.
            </p>
          </div>
          <div className="highlights-grid">
            {highlights.map(({ icon, title, desc }) => (
              <div key={title} className="card highlight-card">
                <div className="highlight-card__icon">{icon}</div>
                <h3 className="highlight-card__title">{title}</h3>
                <p className="highlight-card__desc">{desc}</p>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: 48 }}>
            <Link to="/features" className="btn btn-ghost">
              See All Features <HiOutlineArrowRight />
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section how-it-works">
        <div className="container">
          <div className="section-header">
            <span className="section-label">How It Works</span>
            <h2 className="section-title">Three Simple Steps</h2>
            <p className="section-subtitle">
              Get enhanced results in seconds, not hours.
            </p>
          </div>
          <div className="steps-grid">
            <div className="step-card">
              <div className="step-card__number">01</div>
              <h3 className="step-card__title">Upload</h3>
              <p className="step-card__desc">Drag and drop your images, videos, or documents. Supports bulk upload of 1000+ files.</p>
            </div>
            <div className="step-card__arrow">→</div>
            <div className="step-card">
              <div className="step-card__number">02</div>
              <h3 className="step-card__title">Enhance</h3>
              <p className="step-card__desc">AI auto-detects content type and applies the optimal enhancement. Customize with tuning sliders.</p>
            </div>
            <div className="step-card__arrow">→</div>
            <div className="step-card">
              <div className="step-card__number">03</div>
              <h3 className="step-card__title">Export</h3>
              <p className="step-card__desc">Download in your preferred format — JPG, PNG, WebP, MP4, PDF — with resolution presets.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Use Cases</span>
            <h2 className="section-title">Built for Every Industry</h2>
            <p className="section-subtitle">
              From e-commerce to healthcare, AIScaler adapts to your needs.
            </p>
          </div>
          <div className="usecases-grid">
            {useCases.map(({ title, desc, icon }) => (
              <div key={title} className="card usecase-card">
                <span className="usecase-card__icon">{icon}</span>
                <h3 className="usecase-card__title">{title}</h3>
                <p className="usecase-card__desc">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="section" id="pricing">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Pricing</span>
            <h2 className="section-title">Simple, Transparent Pricing</h2>
            <p className="section-subtitle">Start free, scale as you grow. No hidden fees.</p>
          </div>
          <div className="pricing-grid">
            {plans.map(({ name, price, period, features, cta, highlighted }) => (
              <div key={name} className={`card pricing-card ${highlighted ? 'pricing-card--highlighted' : ''}`}>
                {highlighted && <span className="pricing-card__badge">Most Popular</span>}
                <h3 className="pricing-card__name">{name}</h3>
                <div className="pricing-card__price">
                  <span className="pricing-card__amount">{price}</span>
                  <span className="pricing-card__period">{period}</span>
                </div>
                <ul className="pricing-card__features">
                  {features.map((f) => (
                    <li key={f}><HiOutlineCheck /> {f}</li>
                  ))}
                </ul>
                <Link to="/contact" className={`btn ${highlighted ? 'btn-primary' : 'btn-secondary'} pricing-card__btn`}>
                  {cta}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section cta-section">
        <div className="container">
          <div className="cta-box">
            <h2 className="cta-box__title">Ready to Enhance Your Media?</h2>
            <p className="cta-box__desc">
              Join thousands of professionals using AIScaler to upscale and enhance their content.
            </p>
            <div className="cta-box__actions">
              <Link to="/contact" className="btn btn-primary">Get Started Free</Link>
              <Link to="/features" className="btn btn-secondary">View Features</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
